"use client";

import { ReactNode } from "react";
import Link from "next/link";
import { cn } from "@/lib/utils/utils";
import { motion } from "framer-motion";
import { AppButtonVariant } from "@/lib/types";

// ---------- AppButtonProps ----------
interface AppButtonProps {
  children: ReactNode;
  href?: string;
  onClick?: () => void;
  className?: string;
  buttonClass?: string;
  shadowClass?: string;
  icon?: ReactNode;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  newTab?: boolean;
  variant?: AppButtonVariant;
  block?: boolean;
}

const VARIANT_STYLES: Record<AppButtonVariant, string> = {
  primary: "bg-[#c3de4a] text-black border-2 border-black hover:bg-[#c3de4a]",
  secondary:
    "bg-primary/10 text-primary border border-primary/50 hover:bg-primary/20 hover:border-primary",
  ghost:
    "bg-transparent text-white/80 border border-white/15 hover:border-primary hover:text-primary",
  // Destructive actions only. Lime is the studio's "yes" colour, so a cancel
  // dressed in it reads as confirmation of the wrong thing.
  danger:
    "bg-[#e5484d] text-white border border-[#e5484d] hover:bg-[#cf3b40] hover:border-[#cf3b40]",
};

const AppButton = ({
  children,
  href,
  onClick,
  className,
  buttonClass,
  shadowClass,
  icon,
  type = "button",
  disabled,
  newTab,
  variant = "primary",
  block,
}: AppButtonProps) => {
  // Only the primary variant carries the offset shadow and the lift-on-hover
  // that goes with it — the others live on dark cards where both read as grime.
  const isPrimary = variant === "primary";

  const content = (
    <motion.div
      className={cn(
        "relative group cursor-pointer",
        block ? "block w-full" : "inline-block",
        className,
      )}
      initial="initial"
      whileHover="hover"
      whileTap="tap"
    >
      {isPrimary && (
        /* The Black Shadow (Background Layer) */
        <div
          className={cn(
            "absolute inset-0 bg-black rounded-xs transition-transform duration-200 -translate-x-1 translate-y-1",
            shadowClass,
          )}
        />
      )}

      {/*Top Layer */}
      <motion.div
        variants={
          isPrimary
            ? {
                initial: { x: 0, y: 0 },
                hover: { x: 3, y: -3 },
                tap: { x: 2, y: 2 },
              }
            : { initial: { y: 0 }, hover: { y: -2 }, tap: { y: 0 } }
        }
        transition={{ type: "spring", stiffness: 400, damping: 20 }}
        className={cn(
          "relative flex items-center justify-center gap-2 font-medium uppercase tracking-widest rounded-xs text-sm lg:text-sm transition-colors duration-200",
          VARIANT_STYLES[variant],
          disabled && "pointer-events-none",
          buttonClass,
        )}
      >
        {children} {icon && icon}
      </motion.div>
    </motion.div>
  );
  if (href) {
    return (
      <Link
        href={href}
        // Forward onClick here too: the href branch used to swallow it, so a
        // linked button could never run side effects like closing an open menu.
        onClick={onClick}
        target={newTab ? "_blank" : "_self"}
        rel={newTab ? "noopener noreferrer" : undefined}
        className={cn(
          "cursor-pointer outline-none",
          block ? "block w-full" : "inline-block",
        )}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "cursor-pointer outline-none border-none bg-transparent p-0",
        block ? "block w-full" : "inline-block",
        disabled && "opacity-60 cursor-not-allowed",
      )}
    >
      {content}
    </button>
  );
};

export default AppButton;
